// Yoga Bradford-on-Avon - Town Location Page
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, MapPin, Car } from "lucide-react";
import Navigation from "../../components/shared/Navigation";
import Footer from "../../components/shared/Footer";
import PageBreadcrumb from "../../components/shared/PageBreadcrumb";
import { RetreatPromoBanner } from "../../components/shared/RetreatPromo";
import { usePageMeta, generateLocationSchema, generateFAQSchema, SchemaScript } from "../../components/shared/SEOHelper";

const YogaBradfordOnAvon = () => {
  usePageMeta({
    title: "Yoga Classes Bradford-on-Avon | Annelie Lily",
    description: "Yoga classes for Bradford-on-Avon, Wiltshire. Small group Hatha & Yin yoga in Frome, about 20 minutes away. Personal attention, all levels welcome.",
    canonical: "https://annelielily.com/areas/yoga-bradford-on-avon"
  });

  const locationSchema = generateLocationSchema({
    townName: "Bradford-on-Avon",
    description: "Yoga classes for Bradford-on-Avon at Annelie Lily Yoga in Frome. Small group classes 20 minutes away.",
    url: "https://annelielily.com/yoga-bradford-on-avon"
  });

  const faqs = [
    { question: "How do I get to Frome from Bradford-on-Avon?", answer: "Take the A363 south through Trowbridge, then the A361 to Frome. About 20 minutes through lovely Wiltshire countryside." },
    { question: "What times are classes available?", answer: "I run classes at various times including evenings. Get in touch for the current schedule." },
    { question: "Are classes suitable for beginners?", answer: "Absolutely. I offer modifications for every pose, so you can work at whatever level suits your body today." },
  ];

  const faqSchema = generateFAQSchema(faqs);

  return (
    <div className="min-h-screen bg-cream">
      <SchemaScript schema={locationSchema} />
      <SchemaScript schema={faqSchema} />
      <Navigation />
      <PageBreadcrumb currentPage="Yoga in Bradford-on-Avon" currentSlug="areas/yoga-bradford-on-avon" />
      
      <section className="pt-8 pb-16 px-6 md:px-12 bg-muted">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <span className="text-xs uppercase tracking-[0.2em] text-gold font-semibold mb-3 block">Yoga for Bradford-on-Avon</span>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-light text-neutral-800 mb-5">Yoga Classes Bradford-on-Avon</h1>
            <p className="text-neutral-700 leading-relaxed max-w-2xl mx-auto mb-6">
              Bradford-on-Avon is a beautiful historic town — and Frome is just 20 minutes south. 
              If you're looking for small, personal yoga classes rather than busy gym sessions, 
              you've found the right place.
            </p>
            <div className="flex flex-wrap justify-center gap-4 mb-8">
              <span className="flex items-center gap-2 bg-white/70 px-4 py-2 rounded-full text-sm text-neutral-700"><MapPin className="w-4 h-4 text-gold" />Based in Frome</span>
              <span className="flex items-center gap-2 bg-white/70 px-4 py-2 rounded-full text-sm text-neutral-700"><Car className="w-4 h-4 text-gold" />20 min from Bradford-on-Avon</span>
            </div>
            <Link to="/contact" className="btn-primary">Book a Class <ArrowRight className="w-4 h-4 inline ml-2" /></Link>
          </motion.div>
        </div>
      </section>
      
      <section className="py-16 px-6 md:px-12 bg-cream">
        <div className="max-w-3xl mx-auto">
          <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-6">Yoga for Bradford-on-Avon Residents</h2>
          <div className="prose-content space-y-4 text-neutral-700">
            <p>
              Bradford-on-Avon has plenty of charm, but yoga options can be limited. Frome is 
              just 20 minutes through <Link to="/areas/yoga-trowbridge" className="text-gold hover:underline">Trowbridge</Link> — 
              close enough for a regular practice, and the drive through the countryside makes a 
              nice transition from the busyness of the day.
            </p>
            <p>
              My <Link to="/yoga-classes" className="text-gold hover:underline">yoga classes</Link> are 
              small by design (6-8 people), so there's actually space for individual guidance. 
              I offer <Link to="/yoga-classes/hatha-yoga" className="text-gold hover:underline">Hatha yoga</Link> for 
              active practice and <Link to="/yoga-classes/yin-yoga" className="text-gold hover:underline">Yin yoga</Link> for 
              slower, deeper work.
            </p>
            <p>
              <Link to="/yoga-classes/beginner-yoga" className="text-gold hover:underline">New to yoga?</Link> That's 
              no problem — many of my students started knowing nothing. I teach in a way that 
              welcomes all levels and bodies.
            </p>
          </div>
        </div>
      </section>

      <section className="py-16 px-6 md:px-12 bg-muted">
        <div className="max-w-6xl mx-auto">
          <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-10 text-center">Yoga Classes for Bradford-on-Avon</h2>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { title: "Hatha Yoga", desc: "Traditional practice for strength and flexibility.", link: "/yoga-classes/hatha-yoga" },
              { title: "Yin Yoga", desc: "Slow, deep stretches for release.", link: "/yoga-classes/yin-yoga" },
              { title: "Private Sessions", desc: "One-to-one yoga tailored to you.", link: "/yoga-classes/private-yoga" },
            ].map((service) => (
              <Link key={service.title} to={service.link} className="block bg-white/70 p-6 rounded-xl border border-gold/10 hover:border-gold/30 transition-all group">
                <h3 className="font-heading text-xl text-neutral-800 mb-2 group-hover:text-gold">{service.title}</h3>
                <p className="text-neutral-600 text-sm mb-3">{service.desc}</p>
                <span className="text-gold text-sm font-medium">Learn more →</span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 px-6 md:px-12 bg-cream">
        <div className="max-w-3xl mx-auto">
          <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-8 text-center">Questions from Bradford-on-Avon Students</h2>
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div key={index} className="bg-white/70 p-5 rounded-xl border border-gold/10">
                <h3 className="font-heading text-lg text-neutral-800 mb-2">{faq.question}</h3>
                <p className="text-neutral-600 text-sm">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      
      <section className="py-16 px-6 md:px-12 bg-muted">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-4">Ready to Start?</h2>
          <p className="text-neutral-600 mb-6">Get in touch and I'll help you find the right class.</p>
          <Link to="/contact" className="btn-primary">Book a Class <ArrowRight className="w-4 h-4 inline ml-2" /></Link>
        </div>
      </section>

      <section className="py-12 px-6 md:px-12 bg-cream">
        <div className="max-w-6xl mx-auto text-center">
          <p className="text-neutral-600 text-sm mb-4">Nearby areas:</p>
          <div className="flex flex-wrap justify-center gap-3">
            {[
              { name: "Bath", link: "/areas/yoga-bath" },
              { name: "Trowbridge", link: "/areas/yoga-trowbridge" },
              { name: "Westbury", link: "/areas/yoga-westbury" },
              { name: "Beckington", link: "/areas/yoga-beckington" },
              { name: "Wiltshire", link: "/areas/yoga-wiltshire" },
            ].map((area) => (
              <Link key={area.name} to={area.link} className="text-sm text-gold hover:underline">{area.name}</Link>
            ))}
          </div>
        </div>
      </section>

      {/* Retreat Promotion */}
      <RetreatPromoBanner variant="highlight" />

      <Footer />
    </div>
  );
};

export default YogaBradfordOnAvon;
